// Op type constants — 8-bit code stored in word 0 of each op slot.
export const OP_INSERT_TEXT = 0x01 as const
export const OP_INSERT_ELEMENT = 0x02 as const
export const OP_REMOVE = 0x03 as const
export const OP_SET_TEXT = 0x04 as const
export const OP_REPLACE = 0x05 as const
export const OP_MOVE = 0x06 as const
export const OP_SET_ATTR = 0x07 as const
export const OP_REMOVE_ATTR = 0x08 as const

export type OpType =
    | typeof OP_INSERT_TEXT
    | typeof OP_INSERT_ELEMENT
    | typeof OP_REMOVE
    | typeof OP_SET_TEXT
    | typeof OP_REPLACE
    | typeof OP_MOVE
    | typeof OP_SET_ATTR
    | typeof OP_REMOVE_ATTR

export interface PatchOp {
    opType: OpType
    nodeId: number
    parentId: number
    extra: number
}

// 4 × 32-bit words per op = 16 bytes / op.
const WORDS_PER_OP = 4

// Initial capacity = 16 ops × 4 words.
const INITIAL_CAPACITY_WORDS = 64

/**
 * Write one op into `buf` at word offset `wordOffset` (must be a multiple of 4).
 */
export function encodeOp(
    buf: Uint32Array,
    wordOffset: number,
    opType: OpType,
    nodeId: number,
    parentId: number,
    extra: number,
): void {
    buf[wordOffset] = opType
    buf[wordOffset + 1] = nodeId
    buf[wordOffset + 2] = parentId
    buf[wordOffset + 3] = extra
}

/**
 * Read one op from `buf` at word offset `wordOffset`.
 */
export function decodeOp(buf: Uint32Array, wordOffset: number): PatchOp {
    return {
        opType: buf[wordOffset] as OpType,
        nodeId: buf[wordOffset + 1],
        parentId: buf[wordOffset + 2],
        extra: buf[wordOffset + 3],
    }
}

/**
 * Resizable typed-array-backed buffer for DOM patch ops.
 *
 * The buffer doubles on overflow so growth is amortised O(1). `reset()` keeps
 * the existing ArrayBuffer so repeated render cycles reuse the same allocation.
 */
export class PatchBuffer {
    private _buf: Uint32Array
    private _len = 0 // op count (not word count)

    constructor(initialCapacityWords: number = INITIAL_CAPACITY_WORDS) {
        this._buf = new Uint32Array(initialCapacityWords)
    }

    get length(): number {
        return this._len
    }

    push(opType: OpType, nodeId: number, parentId: number, extra: number): void {
        const wordOffset = this._len * WORDS_PER_OP
        if (wordOffset + WORDS_PER_OP > this._buf.length) {
            const next = new Uint32Array(this._buf.length * 2)
            next.set(this._buf)
            this._buf = next
        }
        encodeOp(this._buf, wordOffset, opType, nodeId, parentId, extra)
        this._len++
    }

    get(index: number): PatchOp {
        return decodeOp(this._buf, index * WORDS_PER_OP)
    }

    reset(): void {
        this._len = 0
    }

    forEach(cb: (op: PatchOp, index: number) => void): void {
        for (let i = 0; i < this._len; i++) {
            cb(decodeOp(this._buf, i * WORDS_PER_OP), i)
        }
    }

    /** Zero-copy view of the live op range. Caller must not mutate after reset. */
    rawSlice(): Uint32Array {
        return this._buf.subarray(0, this._len * WORDS_PER_OP)
    }
}

export function createPatchBuffer(initialCapacityWords?: number): PatchBuffer {
    return new PatchBuffer(initialCapacityWords)
}
